import type { Platform } from "@/platform/Platform";
import { trackEvent } from "./track";
import type {
  AnalyticsDeathReason,
  AnalyticsRunSource,
  AttemptsRemainingBucket,
  ClientErrorSource,
  DailyChallengeOpenedProps,
  DailySubmissionFailureCategory,
  DiscoveryVariant,
  EndlessAnalyticsEndReason,
  EndlessLeaderboardOpenSource,
  EndlessRankBucket,
  GameMode,
  InputMode,
  MarginBucket,
  ResultSource,
  RunFinishedOutcome,
  ScoreChallengeStatusProp,
  ScoreOutcome,
  ShareContext,
  ShareMethod,
  Stars,
} from "./events";

/** Optional Daily Challenge context every gameplay `RunContext` may carry - `undefined` on every field for a campaign run. See `DailyAnalyticsProps` in `events.ts`. */
export interface DailyRunAnalyticsContext {
  challengeId?: string;
  generatorVersion?: number;
  attemptNumber?: number;
  attemptsRemaining?: number;
  layoutId?: string;
  scoringProfileId?: string;
}

function dailyEventProps(daily?: DailyRunAnalyticsContext) {
  return {
    ...(daily?.challengeId !== undefined ? { challenge_id: daily.challengeId } : {}),
    ...(daily?.generatorVersion !== undefined ? { generator_version: daily.generatorVersion } : {}),
    ...(daily?.attemptNumber !== undefined ? { attempt_number: daily.attemptNumber } : {}),
    ...(daily?.attemptsRemaining !== undefined ? { attempts_remaining: daily.attemptsRemaining } : {}),
    ...(daily?.layoutId !== undefined ? { layout_id: daily.layoutId } : {}),
    ...(daily?.scoringProfileId !== undefined ? { scoring_profile_id: daily.scoringProfileId } : {}),
  };
}

/**
 * The application/scene integration layer between the pure engine
 * (`LevelGame`, `core/types.ts` - which never imports this file or
 * anything analytics-related) and the typed event catalog
 * (`events.ts`/`track.ts`). Every function here takes plain
 * already-known values (a snapshot field, a `BiteFeedbackEvent`'s own
 * kind, `RunTracker` context) - none of it reaches into Phaser, so it's
 * unit-testable without a Scene.
 */

export interface RunContext {
  runId: string;
  worldId: string;
  levelId: string;
  /** Which game mode this run is - see `GameMode`'s doc. Every campaign call site passes `CURRENT_GAME_MODE` ("campaign"); Daily Challenge call sites pass `"daily"` explicitly, along with `daily` below. */
  gameMode: GameMode;
  /** Present only for `gameMode: "daily"` runs - see `DailyRunAnalyticsContext`. */
  daily?: DailyRunAnalyticsContext;
  /** PR34: the run's frozen Mobile/Desktop category - sent on `run_started`/`run_finished` only. */
  platform?: Platform;
}

export function trackTutorialCompleted(props: { tutorialVersion: number; inputMode?: InputMode }): void {
  trackEvent("tutorial_completed", {
    tutorial_version: props.tutorialVersion,
    ...(props.inputMode ? { input_mode: props.inputMode } : {}),
  });
}

export function trackRunStarted(
  ctx: RunContext & {
    startingLength: number;
    /** Omit entirely for a Daily survival run - never fabricate a target length it doesn't have. */
    targetLength?: number;
    isReplay: boolean;
    previousBestScore: number;
    previousBestStars: Stars;
    runSource?: AnalyticsRunSource;
  },
): void {
  trackEvent("run_started", {
    run_id: ctx.runId,
    world_id: ctx.worldId,
    level_id: ctx.levelId,
    game_mode: ctx.gameMode,
    starting_length: ctx.startingLength,
    ...(ctx.targetLength !== undefined ? { target_length: ctx.targetLength } : {}),
    ...(ctx.platform ? { platform: ctx.platform } : {}),
    is_replay: ctx.isReplay,
    previous_best_score: ctx.previousBestScore,
    previous_best_stars: ctx.previousBestStars,
    ...(ctx.runSource !== undefined ? { run_source: ctx.runSource } : {}),
    ...dailyEventProps(ctx.daily),
  });
}

export function trackGateOpened(
  ctx: RunContext & { score: number; length: number; elapsedMs: number; steps?: number },
): void {
  trackEvent("gate_opened", {
    run_id: ctx.runId,
    world_id: ctx.worldId,
    level_id: ctx.levelId,
    game_mode: ctx.gameMode,
    score: ctx.score,
    length: ctx.length,
    elapsed_ms: ctx.elapsedMs,
    ...(ctx.steps !== undefined ? { steps: ctx.steps } : {}),
    ...dailyEventProps(ctx.daily),
  });
}

export interface RunFinishedInput {
  outcome: RunFinishedOutcome;
  deathReason?: AnalyticsDeathReason;
  score: number;
  finalLength: number;
  elapsedMs: number;
  activePlayMs: number;
  steps: number;
  starsEarned?: Stars;
  newBestScore: number;
  newBestStars: Stars;
  bestScoreImproved: boolean;
  bestStarsImproved: boolean;
  safeBites: number;
  rushBites: number;
  chaosBites: number;
  maxRushMultiplier: number;
  /** Endless Growth V1 only - see `RunFinishedProps.fastest_step_ms`'s own doc. */
  fastestStepMs?: number;
  /** Endless Growth V1 only - see `RunFinishedProps.endless_new_best`'s own doc. */
  endlessNewBest?: boolean;
  runSource?: AnalyticsRunSource;
}

export function trackRunFinished(ctx: RunContext & RunFinishedInput): void {
  trackEvent("run_finished", {
    run_id: ctx.runId,
    world_id: ctx.worldId,
    level_id: ctx.levelId,
    game_mode: ctx.gameMode,
    outcome: ctx.outcome,
    ...(ctx.deathReason ? { death_reason: ctx.deathReason } : {}),
    score: ctx.score,
    final_length: ctx.finalLength,
    elapsed_ms: ctx.elapsedMs,
    active_play_ms: ctx.activePlayMs,
    steps: ctx.steps,
    ...(ctx.starsEarned !== undefined ? { stars_earned: ctx.starsEarned } : {}),
    new_best_score: ctx.newBestScore,
    new_best_stars: ctx.newBestStars,
    best_score_improved: ctx.bestScoreImproved,
    best_stars_improved: ctx.bestStarsImproved,
    safe_bites: ctx.safeBites,
    rush_bites: ctx.rushBites,
    chaos_bites: ctx.chaosBites,
    max_rush_multiplier: ctx.maxRushMultiplier,
    ...(ctx.fastestStepMs !== undefined ? { fastest_step_ms: ctx.fastestStepMs } : {}),
    ...(ctx.endlessNewBest !== undefined ? { endless_new_best: ctx.endlessNewBest } : {}),
    ...(ctx.platform ? { platform: ctx.platform } : {}),
    ...(ctx.runSource !== undefined ? { run_source: ctx.runSource } : {}),
    ...dailyEventProps(ctx.daily),
  });
}

/**
 * PR33 (Launch & Business Readiness): fired the moment a player taps
 * RETRY/REPLAY (Campaign), PLAY AGAIN (Endless), or TRY AGAIN (Daily) on
 * an already-finished run's result screen - the single, direct signal
 * for "what percentage of finished runs lead to a deliberate replay". See
 * `PlayAgainClickedProps`'s own doc in `events.ts` for why this is a
 * dedicated event rather than reusing `run_started`'s `run_source:
 * "replay"`.
 */
export function trackPlayAgainClicked(props: { gameMode: GameMode }): void {
  trackEvent("play_again_clicked", { game_mode: props.gameMode });
}

// ---------------------------------------------------------------------
// Endless global leaderboard / submission lifecycle events (PR: Endless
// Global Leaderboard V1) - see each property interface's doc in
// `events.ts` for exactly what is/isn't sent. `rank` is only ever turned
// into a coarse bucket here, never sent as an exact number.
// ---------------------------------------------------------------------

/** Never the exact rank, only a coarse bucket - see `EndlessRankBucket`'s own doc. `null` (no result yet, or the lookup itself failed) maps to `"unranked"`. */
export function bucketEndlessRank(rank: number | null): EndlessRankBucket {
  if (rank === null) return "unranked";
  if (rank <= 10) return "top_10";
  if (rank <= 100) return "top_100";
  return "outside_top_100";
}

export function trackEndlessLeaderboardOpened(source: EndlessLeaderboardOpenSource): void {
  trackEvent("endless_leaderboard_opened", { source });
}

export function trackEndlessSubmissionAttempted(endReason: EndlessAnalyticsEndReason): void {
  trackEvent("endless_submission_attempted", { end_reason: endReason });
}

export function trackEndlessSubmissionAccepted(endReason: EndlessAnalyticsEndReason, isNewBest: boolean, rank: number | null): void {
  trackEvent("endless_submission_accepted", { end_reason: endReason, is_new_best: isNewBest, rank_bucket: bucketEndlessRank(rank) });
}

/** A definitive, permanently-rejected (4xx) submission - never retried. */
export function trackEndlessSubmissionRejected(): void {
  trackEvent("endless_submission_rejected", { reason: "validation" });
}

/** A transient failure (network unreachable, 5xx) - the run stays safely queued for a later retry, never dropped. */
export function trackEndlessSubmissionFailed(): void {
  trackEvent("endless_submission_failed", {});
}

/**
 * Fired only alongside a `sent` submission whose server response says
 * `isNewBest: true` - retries can never produce a misleading duplicate:
 * `EndlessLeaderboardService.submitResult()`'s underlying monotonic
 * upsert makes a retry of an already-recorded winning run resolve with
 * `isNewBest: false` (see `record_endless_result()`'s own doc), so the
 * exact same run retried twice can never fire this event twice.
 */
export function trackEndlessNewOnlineBest(rank: number | null): void {
  trackEvent("endless_new_online_best", { rank_bucket: bucketEndlessRank(rank) });
}

/** Fired when the Daily Challenge pre-run screen loads with a resolved challenge - see `DailyChallengeOpenedProps`. Never carries `attempt_number` (fired before any attempt is reserved). */
export function trackDailyChallengeOpened(props: {
  challengeId: string;
  generatorVersion: number;
  biteMode: DailyChallengeOpenedProps["bite_mode"];
  layoutId: string;
  scoringProfileId?: string;
  attemptsRemaining: number;
}): void {
  trackEvent("daily_challenge_opened", {
    challenge_id: props.challengeId,
    generator_version: props.generatorVersion,
    bite_mode: props.biteMode,
    layout_id: props.layoutId,
    ...(props.scoringProfileId !== undefined ? { scoring_profile_id: props.scoringProfileId } : {}),
    attempts_remaining: props.attemptsRemaining,
  });
}

// ---------------------------------------------------------------------
// "Beat My Score" (PR10) sharing/challenge events - see each property
// interface's doc in `events.ts` for exactly what is/isn't sent. Every
// function here takes only already-known, already-bucketed/enum-typed
// values - never a raw score, name, token, or URL - so there is nothing
// for a call site to accidentally leak even if it tried.
// ---------------------------------------------------------------------

export function trackScoreSharePromptViewed(context: ShareContext): void {
  trackEvent("score_share_prompt_viewed", { context });
}

export function trackScoreShareRequested(context: ShareContext): void {
  trackEvent("score_share_requested", { context });
}

export function trackScoreShareSucceeded(context: ShareContext, method: ShareMethod): void {
  trackEvent("score_share_succeeded", { context, method });
}

export function trackScoreShareCancelled(context: ShareContext): void {
  trackEvent("score_share_cancelled", { context });
}

export function trackScoreShareFailed(context: ShareContext, method: ShareMethod): void {
  trackEvent("score_share_failed", { context, method });
}

export function trackScoreShareLinkCopied(context: ShareContext): void {
  trackEvent("score_share_link_copied", { context });
}

export function trackScoreChallengeOpened(props: { status: ScoreChallengeStatusProp; selfReferral: boolean; returningPlayer: boolean }): void {
  trackEvent("score_challenge_opened", { status: props.status, self_referral: props.selfReferral, returning_player: props.returningPlayer });
}

export function trackScoreChallengeCtaClicked(attemptsRemainingBucket: AttemptsRemainingBucket): void {
  trackEvent("score_challenge_cta_clicked", { attempts_remaining_bucket: attemptsRemainingBucket });
}

export function trackScoreChallengeRunStarted(attemptsRemainingBucket: AttemptsRemainingBucket, generatorVersion: number): void {
  trackEvent("score_challenge_run_started", { attempts_remaining_bucket: attemptsRemainingBucket, generator_version: generatorVersion });
}

export function trackScoreChallengeVerifiedResult(outcome: ScoreOutcome, marginBucket: MarginBucket, generatorVersion: number): void {
  trackEvent("score_challenge_verified_result", { outcome, margin_bucket: marginBucket, generator_version: generatorVersion });
}

export function trackScoreChallengeReshared(outcome: ScoreOutcome): void {
  trackEvent("score_challenge_reshared", { outcome });
}

// ---------------------------------------------------------------------
// Instant Play & Onboarding V1 - entry funnel, movement onboarding, and
// worlds-discovery events. See each property interface's doc in
// `events.ts` for exactly when each fires.
// ---------------------------------------------------------------------

export function trackPlayNowClicked(): void {
  trackEvent("play_now_clicked", {});
}

export function trackFirstInputReceived(props: { gameMode: GameMode; inputMode: InputMode }): void {
  trackEvent("first_input_received", { game_mode: props.gameMode, input_mode: props.inputMode });
}

export function trackWorldsDiscoveryClicked(resultSource: ResultSource, discoveryVariant?: DiscoveryVariant): void {
  trackEvent("worlds_discovery_clicked", { result_source: resultSource, ...(discoveryVariant !== undefined ? { discovery_variant: discoveryVariant } : {}) });
}

// ---------------------------------------------------------------------
// PR28: minimal client-side error observability. Called only from
// `src/analytics/errorReporting.ts`, which owns all sanitization/
// truncation before this point - never called directly from a scene.
// ---------------------------------------------------------------------

export function trackClientError(props: { source: ClientErrorSource; message: string; stack?: string; path: string }): void {
  trackEvent("client_error", {
    source: props.source,
    message: props.message,
    ...(props.stack !== undefined ? { stack: props.stack } : {}),
    path: props.path,
  });
}

/** Daily's own equivalent of `trackEndlessSubmissionFailed()` - see `DailySubmissionFailedProps`'s own doc. Called only from `DailyResultScene.submit()`'s failure branch. */
export function trackDailySubmissionFailed(props: {
  challengeId: string;
  generatorVersion: number;
  attemptNumber: number;
  failureCategory: DailySubmissionFailureCategory;
  retryable: boolean;
}): void {
  trackEvent("daily_submission_failed", {
    challenge_id: props.challengeId,
    generator_version: props.generatorVersion,
    attempt_number: props.attemptNumber,
    failure_category: props.failureCategory,
    retryable: props.retryable,
  });
}

/**
 * Guarantees "at most one `run_finished` per run" independent of which
 * scene-lifecycle path (death, completion, or the scene tearing down
 * mid-run) gets there first - a plain latch, deliberately extracted as
 * its own small, framework-free class so this exact guarantee is
 * unit-testable without a Phaser `Scene`. Each `GameScene` run owns
 * exactly one instance.
 */
export class RunFinishedGuard {
  private emitted = false;

  get hasEmitted(): boolean {
    return this.emitted;
  }

  /** Runs `emit` only the first time this is ever called; every call after that is a no-op. */
  emitOnce(emit: () => void): void {
    if (this.emitted) return;
    this.emitted = true;
    emit();
  }
}
